import { reactive, markRaw, shallowReactive, defineAsyncComponent, h } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import ErrorBoundary from '@/shared/components/ErrorBoundary.vue';
import CFSpinner from '@/shared/components/CFSpinner.vue';

function _useModal() {
    const overlays = shallowReactive([]);
    const overlayMap = new Map();

    const create = (component, _options = undefined) => {
        const { props, defaultOpen, destroyOnClose } = _options || {};

        // Handle dynamic imports with error handling
        const resolvedComponent =
            typeof component === 'function'
                ? markRaw(
                      defineAsyncComponent({
                          loader: component,
                          errorComponent: {
                              setup() {
                                  return () => h(ErrorBoundary);
                              },
                          },
                          loadingComponent: {
                              setup() {
                                  return () =>
                                      h(
                                          'div',
                                          { class: 'flex items-center justify-center p-8' },
                                          h(CFSpinner, { size: 'large' })
                                      );
                              },
                          },
                          delay: 200,
                          timeout: 10000,
                      })
                  )
                : markRaw(component);

        const options = reactive({
            id: Symbol(import.meta.dev ? 'useModal' : ''),
            isOpen: !!defaultOpen,
            component: resolvedComponent,
            isMounted: !!defaultOpen,
            destroyOnClose: !!destroyOnClose,
            originalProps: props || {},
            props: { ...(props || {}) },
            resolvePromise: undefined,
        });

        overlays.push(options);
        overlayMap.set(options.id, options);

        return {
            ...options,
            open: (props = undefined) => open(options.id, props),
            close: (value) => close(options.id, value),
            patch: (props) => patch(options.id, props),
        };
    };

    const open = (id, props = undefined) => {
        const overlay = getOverlay(id);
        if (!overlay) {
            return null;
        }

        // Clean up any existing promise before creating new one
        if (overlay.resolvePromise) {
            overlay.resolvePromise = undefined;
        }

        // If props are provided, update the overlay's props
        if (props) {
            patch(overlay.id, { ...overlay.originalProps, ...props });
        } else {
            patch(overlay.id, overlay.originalProps);
        }

        overlay.isOpen = true;
        overlay.isMounted = true;

        return {
            id,
            isMounted: overlay.isMounted,
            isOpen: overlay.isOpen,
            result: new Promise((resolve) => (overlay.resolvePromise = resolve)),
        };
    };

    const close = (id, value = undefined) => {
        const overlay = getOverlay(id);
        if (!overlay) {
            return;
        }

        overlay.isOpen = false;

        // Robust promise cleanup
        if (overlay.resolvePromise) {
            try {
                overlay.resolvePromise(value);
            } catch (error) {
                console.warn('Error resolving modal promise:', error);
            } finally {
                overlay.resolvePromise = undefined;
            }
        }
    };

    const closeAll = () => {
        overlays.forEach((overlay) => close(overlay.id));
    };

    const unmount = (id) => {
        const overlay = getOverlay(id);
        if (!overlay) {
            return;
        }

        overlay.isMounted = false;

        if (overlay.destroyOnClose) {
            // Clean up promise if still pending
            if (overlay.resolvePromise) {
                overlay.resolvePromise = undefined;
            }

            // Remove from both array and map
            const index = overlays.findIndex((overlay) => overlay.id === id);
            if (index !== -1) {
                overlays.splice(index, 1);
            }
            overlayMap.delete(id);
        }
    };

    const patch = (id, props) => {
        const overlay = getOverlay(id);
        if (!overlay) {
            return;
        }

        overlay.props = { ...props };
    };

    const getOverlay = (id) => {
        const overlay = overlayMap.get(id);

        if (!overlay) {
            console.warn(`Modal overlay with id ${id.toString()} not found`);
            return null;
        }

        return overlay;
    };

    const isOpen = (id) => {
        const overlay = getOverlay(id);

        return overlay ? overlay.isOpen : false;
    };

    return {
        overlays,
        open,
        close,
        closeAll,
        create,
        patch,
        unmount,
        isOpen,
    };
}

export const useModal = /* @__PURE__ */ createSharedComposable(_useModal);
